/**
 * MATCH interfaces and types
 */

import { IReferee } from './player';
import { ITeamTypings } from './team';

/**
 * IMatch interface
 * Contains all the info for a match
 * @param away - Object with all info for the AWAY team
 * @param date - Expected match starting Date/Time in ISO-8601 UTC format (for PreMatch status)
 * @param home - Object with all info for the HOME team
 * @param id - Match ID
 * @param matchCurrentDateTime - Actual Date/Time in ISO-8601 UTC format (for LIVE timer)
 * @param referees - Array of referees
 * @param roundGroup - Round Group (for tournament rounds such as Europe and Champions Leagues)
 * @param status - Current match status/period
 * @param statusLabel - Status localization
 * @param statusStartDateTime - Real match starting Date/Time in ISO-8601 UTC format (for LIVE statuses)
 */
export interface IMatch {
  away: ITeamTypings;
  date?: string;
  home: ITeamTypings;
  id?: string | number; // TODO: Should be only NUMBER after BE close ISS-4186 (and we'll fix our mocked data accordingly)
  matchId?: string | null; // FIXME: After DYNAMO migration ID is now MATCHID, should FIX all MOCKS and TESTS!
  matchCurrentDateTime?: string;
  referees?: Array<IReferee>;
  roundGroup?: string | null;
  status: MatchStatusType;
  statusLabel?: string | null;
  statusStartDateTime?: string;
  liveOnSky?: LiveOnSkyType;
}

export const FIRSTHALF: MatchStatusLiveType = 'FirstHalf'; // Live - First Half
export const SECONDHALF: MatchStatusLiveType = 'SecondHalf'; // Live - Second Half
export const EXTRAFIRSTHALF: MatchStatusLiveType = 'ExtraFirstHalf'; // Live Overtime - First Half
export const EXTRASECONDHALF: MatchStatusLiveType = 'ExtraSecondHalf'; // Live Overtime - Second Half
export const PREMATCH: MatchStatusType = 'PreMatch'; // Match not started yet
export const LIVE: MatchStatusType = 'Live'; // Match is Live
export const HALFTIME: MatchStatusType = 'HalfTime'; // Live - Half Time
export const FULLTIME: MatchStatusType = 'FullTime'; // Match ended (after Regular Time, Over Time or Shootouts)
export const FULLTIME90: MatchStatusType = 'FullTime90'; // Match ended but it's going to Over Time
export const EXTRAHALFTIME: MatchStatusType = 'ExtraHalfTime'; // Live Overtime - Half Time
export const FULLTIMEAFTEROVERTIME: MatchStatusType = 'FullTimeAfterOverTime'; // Match ended after Over Time
export const FULLTIMEPENS: MatchStatusType = 'FullTimePens'; // Match ended but it's going to Shootouts
export const SHOOTOUT: MatchStatusType = 'ShootOut'; // Live - Shootout Time (after Overtime)
export const FULLTIMEAFTERSHOOTOUT: MatchStatusType = 'FullTimeAfterShootOut'; // Match ended after Shootouts
export const POSTPONED: MatchStatusType = 'Postponed'; // Match is postponed (never started)
export const ABANDONED: MatchStatusType = 'Abandoned'; // Match is abandoned (after started)
export const CANCELLED: MatchStatusType = 'Cancelled'; // Match is cancelled (never started)

export const MatchStatusLiveList: MatchStatusLiveType[] = [FIRSTHALF, SECONDHALF, EXTRAFIRSTHALF, EXTRASECONDHALF];
export const MatchStatusList: MatchStatusType[] = [
  ...MatchStatusLiveList,
  PREMATCH,
  LIVE,
  HALFTIME,
  FULLTIME,
  FULLTIME90,
  EXTRAHALFTIME,
  FULLTIMEAFTEROVERTIME,
  FULLTIMEPENS,
  SHOOTOUT,
  FULLTIMEAFTERSHOOTOUT,
  POSTPONED,
  ABANDONED,
  CANCELLED,
];

export const liveArray: MatchStatusType[] = [
  LIVE,
  FIRSTHALF,
  HALFTIME,
  SECONDHALF,
  FULLTIME90,
  EXTRAFIRSTHALF,
  EXTRAHALFTIME,
  EXTRASECONDHALF,
  FULLTIMEPENS,
  SHOOTOUT,
];

// TODO: Check with UX which STATUSES have CHRONO icon!
export const hasChronoArray: MatchStatusType[] = [
  FIRSTHALF,
  HALFTIME,
  SECONDHALF,
  FULLTIME90,
  EXTRAFIRSTHALF,
  EXTRASECONDHALF,
  EXTRAHALFTIME,
  FULLTIMEPENS,
  SHOOTOUT,
];

export const notEndedArray: MatchStatusType[] = [ABANDONED, CANCELLED, POSTPONED];
export const endedArray: MatchStatusType[] = [FULLTIME, FULLTIMEAFTEROVERTIME, FULLTIMEAFTERSHOOTOUT];
export const regularTimeArray: MatchStatusType[] = [HALFTIME, FIRSTHALF, SECONDHALF];
export const extraTimeArray: MatchStatusType[] = [FULLTIME90, EXTRAHALFTIME, EXTRAFIRSTHALF, EXTRASECONDHALF];
export const shootoutArray: MatchStatusType[] = [FULLTIMEPENS, SHOOTOUT];
export const betweenPhases: MatchStatusType[] = [FULLTIME90, EXTRAHALFTIME, HALFTIME, ...shootoutArray];
export const hasTimerArray: MatchStatusType[] = [FIRSTHALF, SECONDHALF, EXTRAFIRSTHALF, EXTRASECONDHALF];

export type MatchStatusLiveType = 'FirstHalf' | 'SecondHalf' | 'ExtraFirstHalf' | 'ExtraSecondHalf';

export type MatchStatusType =
  | MatchStatusLiveType
  | 'PreMatch'
  | 'Live'
  | 'HalfTime'
  | 'FullTime'
  | 'FullTime90'
  | 'ExtraHalfTime'
  | 'FullTimeAfterOverTime'
  | 'FullTimePens'
  | 'ShootOut'
  | 'FullTimeAfterShootOut'
  | 'Postponed'
  | 'Abandoned'
  | 'Cancelled';

export type MatchPhaseRound = undefined | null | 'Andata' | 'Ritorno';
export const FIRST_LEG: MatchPhaseRound = 'Andata';
export const SECOND_LEG: MatchPhaseRound = 'Ritorno';
export const matchPhaseArray: MatchPhaseRound[] = [null, FIRST_LEG, SECOND_LEG];

// TODO: 2020-11-03 - Manage NON LINEAR PHASES and KNOCKOUT
export type MatchPhase =
  | null
  | undefined
  | 'Finale'
  | 'Finale 3° posto'
  | 'Finale Playoff'
  | 'Ottavi'
  | 'Playout'
  | 'Preliminari'
  | 'Quarti'
  | 'Sedicesimi'
  | 'Semifinale Playoff'
  | 'Semifinali'
  | 'Spareggi'
  | 'Turni'
  | 'Turno Preliminare Playoff';

// NON LINEAR COMPETITION PHASES
export const ROUNDS: MatchPhase = 'Turni';
export const PRELIMINARIES: MatchPhase = 'Preliminari';
export const PRELIMINARY_ROUND_PLAYOFF: MatchPhase = 'Turno Preliminare Playoff';
export const PLAYOUT: MatchPhase = 'Playout';
export const ROUND_OF_16: MatchPhase = 'Sedicesimi';
export const EIGHTS: MatchPhase = 'Ottavi';
export const QUARTERS: MatchPhase = 'Quarti';
export const SEMIFINALS: MatchPhase = 'Semifinali';
export const SEMIFINAL_PLAYOFF: MatchPhase = 'Semifinale Playoff';
export const FINAL: MatchPhase = 'Finale';
export const FINAL_PLAYOFF: MatchPhase = 'Finale Playoff';

export const nonLinearCompetitionPhasesArray: MatchPhase[] = [
  null,
  ROUNDS,
  PRELIMINARIES,
  PRELIMINARY_ROUND_PLAYOFF,
  PLAYOUT,
  ROUND_OF_16,
  EIGHTS,
  QUARTERS,
  SEMIFINALS,
  SEMIFINAL_PLAYOFF,
  FINAL,
  FINAL_PLAYOFF,
];

export type LiveOnSkyType = 'LIVE' | 'EXCLUSIVE';
export const LIVE_ON_SKY: LiveOnSkyType = 'LIVE';
export const EXCLUSIVE_ON_SKY: LiveOnSkyType = 'EXCLUSIVE';

export const liveOnSkyArray: LiveOnSkyType[] = [LIVE_ON_SKY, EXCLUSIVE_ON_SKY];

export type RoundName = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I' | 'J' | 'K' | 'L' | 'unico';
export const roundNameList: Array<RoundName> = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'unico'];
